import React from 'react';
import {
  SearchOutlined,
  TrophyOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  CustomerServiceOutlined,
  BulbOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import useReveal from '../../AIEmployees/useReveal';
import { BENCHMARK_MONTH, BENCHMARK_URL } from '../../../content/benchmarkClaim';
import './SAResearch.css';

/**
 * SAResearch — "Reasoning that travels." The thesis beat: CellCog isn't a
 * deep-research company; it's building general AI employees, and deep research
 * is simply where its reasoning gets *measured*. The benchmark rank is a proof
 * point — the same reasoning travels into every modality.
 *
 * Ranking claim is rendered point-in-time (dated card): the latest submission
 * is verified at #1, month from content/benchmarkClaim.json (the ONE source).
 * We link the live board rather than freezing a
 * contestable static chart, and keep the track-record line so the claim stays
 * honest as the board churns.
 *
 * NOTE: also composed on the homepage hub (components/Home/HomePage) as the
 * top-of-page trust band — copy changes here appear on BOTH pages.
 */
const TRAVELS = [
  { Icon: EyeOutlined, label: 'Vision' },
  { Icon: VideoCameraOutlined, label: 'Video' },
  { Icon: AudioOutlined, label: 'Audio' },
  { Icon: CustomerServiceOutlined, label: 'Music' },
  { Icon: BulbOutlined, label: 'Strategy' },
  { Icon: ReadOutlined, label: 'Story' },
];

const SAResearch = () => {
  const [ref, visible] = useReveal();
  return (
    <section className="sa-research aie-section" ref={ref}>
      <div className={`sa-research__inner aie-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="sa-research__copy">
          <span className="aie-eyebrow">
            <SearchOutlined /> Why it&rsquo;s actually good
          </span>
          <h2 className="sa-research__title aie-display">
            We&rsquo;re not a deep-research company.
            <br />
            We just happen to be #1 at it.
          </h2>
          <p className="sa-research__body">
            We&rsquo;re building AI employees &mdash; workers that run in the
            background across every kind of task. That only works if deep
            reasoning is fused with every modality, not bolted onto text. Deep
            research is simply where that reasoning gets <em>measured</em>, and
            we&rsquo;ve climbed back to <strong>#1 on DeepResearch Bench</strong>
            {' '}several times this past year, most recently {BENCHMARK_MONTH}. The same reasoning that tops a
            research benchmark is what directs a video, scores a piece of music,
            or plots a strategy.
          </p>
          <a
            className="sa-research__link"
            href={BENCHMARK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            See the live leaderboard <ArrowRightOutlined />
          </a>
        </div>

        <div className="sa-research__card">
          <div className="sa-research__card-head">
            <TrophyOutlined />
            <span>DeepResearch Bench</span>
          </div>
          <div className="sa-research__badge">
            <span className="sa-research__badge-rank">#1</span>
            <div className="sa-research__badge-meta">
              <span className="sa-research__badge-board">DeepResearch Bench</span>
              <span className="sa-research__badge-date">{BENCHMARK_MONTH}</span>
            </div>
          </div>
          <p className="sa-research__track">
            A real track record: we&rsquo;ve held #1 several times over the past
            year, ranging from #1 to #5 as the field moves fast &mdash; and our
            latest submission is verified back at #1.
          </p>

          <div className="sa-research__travels">
            <span className="sa-research__travels-label">
              The same reasoning travels into
            </span>
            <div className="sa-research__travels-chips">
              {TRAVELS.map(({ Icon, label }) => (
                <span className="sa-research__travel" key={label}>
                  <Icon /> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SAResearch;
